import {inject, injectable} from "inversify";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import SystemConfig, {CONFIG_PARAMS, MODES} from "../system/SystemConfig";
import Connect from "../connect/Connect";
import Wallet, {WALLET_EVENTS} from "../wallet/Wallet";
import IEventObserver from "../events/IEventObserver";
import AbstractAudio from "../engine/AbstractAudio";
import events from "../events/EventsObservable";
import {ACTION_EVENTS} from "../ui/UserAction";
import {getDeviceStr, mobileAndTabletCheck} from "../device/device";
import AbstractUI from "../ui/AbstractUI";
import {EVENTS} from "../events/baseEvents";
import {SCREEN_EVENTS} from "../ui/screen/AbstractUIScreen";
import UIController from "../ui/UIController";
import * as calc from "../wallet/walletOperations";
import {MESSAGE_EVENTS} from "../error/errorMessages";
import BaseDOM, {BASE_ELEMENT_IDS} from "../ui/BaseDOM";
import MobileUIController from "../ui/mobile/MobileUIController";
import {PLAYER_EVENTS} from "./playerEvents";
import UIMessages from "../ui/messages/UIMessages";
import MobileDOM from "../ui/mobile/MobileDOM";
import { GAME_EVENTS } from '../game/gameEvents'
import {PLATFORM} from "../system/SystemPlatform";
import {getClosestValue} from "../helpers";

@injectable()
class PlayerOptions implements IEventObserver {

    protected config: SystemConfig;
    protected connect: Connect;
    protected wallet: Wallet;
    protected audio: AbstractAudio;
    protected dom: BaseDOM;
    protected uiController: UIController;

    protected options: IPlayerOptionsData;  // last option sent to server

    protected isFirstTime: boolean = false;
    firstTimeResolve: any = undefined;

    constructor(
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.Connect) connect: Connect,
        @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
        @inject(SERVICE_IDENTIFIER.EngineAudio) audio: AbstractAudio,
        @inject(SERVICE_IDENTIFIER.DOM) dom: BaseDOM,
        @inject(SERVICE_IDENTIFIER.UIController) uiController: UIController,
        @inject(SERVICE_IDENTIFIER.UIMessages) uiMessages: UIMessages
    ) {
        this.config = config;
        this.connect = connect;
        this.wallet = wallet;
        this.audio = audio;
        this.dom = dom;
        this.uiController = uiController;
    }

    /**
     * If player_id and session_id is set, fetch settings from server and set this.options.
     */
    async init() {
        this.initOptions();
        if (this.isAllowed()) {
            // let last = await this.connect.getLastOpts({});
            // this.setOptionsData(last.data);

            let game = await this.connect.getGameOpt({});
            this.setOptionsData(game.data);

            let device = await this.connect.getDeviceOpt({});
            this.setOptionsData(device.data);

            if (mobileAndTabletCheck()) {
                let button = await this.connect.getButtonOpt({button: "spin"});
                if (button.status === 204) {
                    this.isFirstTime = true;
                } else if (button.status === 200) {
                    this.setButtonsData(button.data);
                }
            }
        }
    }

    /**
     * Is settings.player_options set to true and are session id and player id set.
     */
    isAllowed() {
        return this.config.get(CONFIG_PARAMS.SESSION_ID) &&
               this.config.get(CONFIG_PARAMS.PLAYER_ID) !== undefined &&
               this.config.get(CONFIG_PARAMS.PLAYER_OPTIONS) &&
               this.config.get(CONFIG_PARAMS.MODE) === MODES.REAL
    }

    /**
     * Set bet and sound by this.options values.
     * If mobile or tablet sounds are set, disable the sound choosing message.
     * Subscribe to events.
     */
    load() {
        if (this.isAllowed()) {
            // If mobile or tablet sounds are set, disable the sound choosing message.
            if (this.options.devices[getDeviceStr()].sound !== undefined) {
                if ((this.dom as MobileDOM)?.soundChooseMessage?.closeMessage && mobileAndTabletCheck()) {
                   (this.dom as MobileDOM).soundChooseMessage.closeMessage();
                }
            }

            this.setSound();
            this.setBet();
            this.setHand();
            this.setSpinButtonPosition();

            // subscribe to events
            events.subscribe(WALLET_EVENTS.WALLET_ON_SET_BET, this);
            events.subscribe(ACTION_EVENTS.ACTION_SOUND_TOGGLE, this);
            events.subscribe(EVENTS.SPIN, this);
            events.subscribe(EVENTS.CLOSE_GAME, this);
            window.addEventListener("unload", this.unload.bind(this));
            window.addEventListener("beforeunload", this.unload.bind(this));
            events.subscribe(SCREEN_EVENTS.SCREEN_VISIBILITY_CHANGE, this);
            events.subscribe(MESSAGE_EVENTS.SWITCH_TO_REAL, this);
        }
        events.subscribe(ACTION_EVENTS.ACTION_HANDEDNESS_TOGGLE, this);
        events.subscribe(PLAYER_EVENTS.SPIN_POSITION_SET, this);
        window.addEventListener("resize", this.onResize.bind(this));
    }

    firstSpinButton(): Promise<void> {
        return new Promise<void>(res => {
            if (this.isFirstTime && (this.dom as MobileDOM)?.isSettingsMenuAllowed()) {
                events.dispatch(ACTION_EVENTS.ACTION_SETTINGS_OPEN);
                this.firstTimeResolve = res;
                events.subscribe(ACTION_EVENTS.ACTION_SETTINGS_SAVE, this.firstSpinButtonSave.bind(this));
                events.subscribe(ACTION_EVENTS.ACTION_SETTINGS_CLOSE, this.firstSpinButtonSave.bind(this));
            } else {
                res();
            }
        });
    }

    firstSpinButtonSave(eventName: string) {
        if (this.firstTimeResolve) {
            if (eventName === ACTION_EVENTS.ACTION_SETTINGS_CLOSE) {
                this.sendSpinButtonData();
            }
            this.firstTimeResolve && this.firstTimeResolve();
            this.firstTimeResolve = undefined;
        }
    }

    /**
     * Handle events.
     * Send bet values to server if changed on spin.
     * Send bet and sound if changed on window.close event.
     * Send bet and sound if changed on window.visibilityChagne event.
     * @param eventName
     * @param data
     */
    async notify(eventName: string, data: any) {
        switch (eventName) {
            case ACTION_EVENTS.ACTION_SOUND_TOGGLE:
                this.updateSound();
                break;
            case ACTION_EVENTS.ACTION_HANDEDNESS_TOGGLE:
                this.updateHand();
                this.setSpinButtonPosition();
                break;
            case EVENTS.SPIN:
                if (this.isBetChanged() && !this.wallet.isBonus()) {
                    this.sendWalletData();
                }
                this.updateOptions();
                break;
            case EVENTS.CLOSE_GAME:
                this.sendWalletData(true);
                this.sendDeviceData(true);
                this.updateOptions();
                break;
            case SCREEN_EVENTS.SCREEN_VISIBILITY_CHANGE:
                if (!data.focus) {
                    this.sendWalletData();
                    this.sendDeviceData();
                }
                break;
            case MESSAGE_EVENTS.SWITCH_TO_REAL:
                this.setBet(true);
                break;
            case PLAYER_EVENTS.SPIN_POSITION_SET:
                this.updateSpinButtonPosition(data.top, data.right);
                if (this.isAllowed()) {
                    this.sendSpinButtonData();
                }
                break;
            default:
                break;
        }
    }

    /**
     * Called when closing the window. Update this.options values and send them to server using
     * navigator.beacon.
     */
    async unload() {
        this.sendWalletData(true);
        this.sendDeviceData(true);
        this.updateOptions();
    }

    onResize() {
        this.setSpinButtonPosition();
    }

    /**
     * Check if bet has changed from the last sent value.
     */
    isBetChanged() {
        return this.options.bet !== this.wallet.getCreditBet() ||
            this.options.coin !== this.wallet.getCoin() ||
            this.options.lines_count !== this.wallet.getLinesCount();
    }

    /**
     * Check if sound has changed from the last sent value.
     */
    isSoundChanged() {
        return this.options.devices[getDeviceStr()].sound !== !this.audio.isMuted();
    }

    /**
     * Update this.options with current bet settings. Send new bet settings to server.
     * @param beacon {boolean} switch between beacon or normal fetch
     */
    async sendWalletData(beacon: boolean = false) {
        this.updateBet();
        try {
            await this.connect.setGameOpt({
                ...this.options,
                "session_id": this.config.get(CONFIG_PARAMS.SESSION_ID),
                "beacon": beacon
            });
        } catch(e) {
            console.error(e);
        }
    }

    /**
     * Update this.options with current sound setting. Send new device sound settings to server.
     * @param beacon {boolean} switch between beacon or normal fetch
     */
    async sendDeviceData(beacon: boolean = false) {
        try {
            await this.connect.setDeviceOpt({
                "hand": this.options.devices[getDeviceStr()].hand,
                "sound": this.options.devices[getDeviceStr()].sound,
                "session_id": this.config.get(CONFIG_PARAMS.SESSION_ID),
                "beacon": beacon
            });
        } catch(e) {
            console.error(e);
        }
    }

    async sendSpinButtonData() {
        try {
            await this.connect.setButtonOpt({
                "session_id": this.config.get(CONFIG_PARAMS.SESSION_ID),
                "button": "spin",
                "orientations": this.replaceNullWith({...this.options.buttons.spin.orientations}, -1)
            });
        } catch(e) {
            console.error(e);
        }
    }

    replaceNullWith(obj: Object, val: any) {
        Object.keys(obj).forEach((key) => {
            if(obj[key] === null) {
                obj[key] = val;
            } else if (typeof obj[key] == "object") {
                obj[key] = this.replaceNullWith(obj[key], val);
            }
        });
        return obj;
    }

    /**
     * Update both bet and sound settings.
     */
    updateOptions() {
        this.updateBet();
        this.updateSound();
    }

    /**
     * Update this.options device sound value with data from audio.
     */
    updateSound() {
        this.options.devices[getDeviceStr()].sound = !this.audio.isMuted();
    }

    /**
     * Update this.options device hand value with data from uiController
     */
    updateHand() {
        this.options.devices[getDeviceStr()].hand = this.uiController.getHand();
    }

    updateSpinButtonPosition(top: number, right: number) {
        let mode = document.getElementById(BASE_ELEMENT_IDS.WRAPPER).classList.contains("mode-portrait")? "portrait" : "landscape";
        let hand = this.uiController.getHand();
        this.options.buttons.spin.orientations[mode].hands[hand].position.top = top === null ? -1 : top;
        this.options.buttons.spin.orientations[mode].hands[hand].position.right = right === null ? -1 : right;
    }

    /**
     * Update this.options bet values with data from wallet.
     */
    updateBet() {
        if (!this.wallet.isBonus()) {
            this.options.bet = this.wallet.getCreditBet();
            this.options.coin = this.wallet.getCoin();
            this.options.lines_count = this.wallet.getLinesCount();
        }
    }

    /**
     * Set sound by device this.options sound setting.
     */
    setSound() {
        if (this.options.devices[getDeviceStr()].sound) {
            this.audio.unmute();
        } else {
            this.audio.mute();
        }
    }

    /**
     * Set handedness by device this.options hand setting
     */
    setHand() {
        if (this.options.devices[getDeviceStr()].hand === "left") {
            this.uiController.setLeftHandedUI();
        } else {
            this.uiController.setRightHandedUI();
        }
    }

    /**
     * Set wallet values according to this.options.
     * If bet in this.options is not valid, find nearest valid bet and use it instead.
     */
    setBet(force: boolean = false) {
        // Disable player options on bet settings if in only coins mode
        if (this.config.get(CONFIG_PARAMS.ONLY_COINS) == 1) {
            return;
        }
        if (!this.wallet.isBonus() || force) {
            let bet = calc.getMoneyBet(this.options.bet, this.options.coin, this.wallet.getDecimalPlaces());
            if (!this.walletSetBet(bet))  {
                let betInReal = this.options.bet * this.options.coin
                let validBet = getClosestValue(this.wallet.getAllowedBets(), betInReal)
                if (!this.walletSetBet(validBet)) {
                    console.warn("PlayerOptions: Epic fail, can't find nearest valid bet.");
                }
                console.warn("PlayerOptions: Using nearest valid bet.", validBet);
            }
        }
    }

	walletSetBet(bet: number): boolean {
		if (this.wallet.isBetInBounds(bet, this.options.lines_count)) {
			let data = this.wallet.calcDataFromBet(bet)
			this.options.bet = data.creditBet
			this.options.coin = data.coin
			this.options.lines_count = data.linesCount
			this.wallet.setCoin(this.options.coin, this.options.bet, true);
			if (this.wallet.getLines().includes(this.options.lines_count)) {
				this.wallet.setLinesCount(this.options.lines_count);
			} else {
				if (this.wallet.getLines()[0]) {
					this.wallet.setLinesCount(this.wallet.getLines()[0]);
				} else {
					this.wallet.setLinesCount(1);
				}
			}
			this.wallet.recalc();
			return true
		} else {
			return false
		}
	}

    setSpinButtonPosition() {
        if (this.config.get(CONFIG_PARAMS.UI_DEVICE) !== PLATFORM.DESKTOP) {
            let mode = document.getElementById(BASE_ELEMENT_IDS.WRAPPER).classList.contains("mode-portrait")? "portrait" : "landscape";
            let hand = this.uiController.getHand();
            if (
                this.options.buttons.spin.orientations[mode].hands[hand].position.top === -1 &&
                this.options.buttons.spin.orientations[mode].hands[hand].position.right === -1
            ) {
                (this.uiController as MobileUIController).settingsSetPosition();
            } else {
                (this.uiController as MobileUIController).settingsSetPosition(
                    this.options.buttons.spin.orientations[mode].hands[hand].position.top,
                    this.options.buttons.spin.orientations[mode].hands[hand].position.right
                );
            }
        }
    }

    /**
     * Set this.options with data from server
     * @param data {object} response from server (last, game and device options)
     */
    setOptionsData(data: any) {
        // overwrite options by data
        this.options.bet = data?.bet ? data.bet : this.options.bet;
        this.options.coin = data?.coin ? data.coin : this.options.coin;
        this.options.lines_count = data?.lines_count ? data.lines_count : this.options.lines_count;
        this.options.devices[getDeviceStr()].sound = data.sound;
        this.options.devices[getDeviceStr()].hand = data.hand;
    }

    setButtonsData(data: any) {
        // overwrite options by data
        for (let mode of ["landscape", "portrait"]) {
            for (let hand of ["right", "left"]) {
                this.options.buttons.spin.orientations[mode].hands[hand].position.top = data?.orientations[mode]?.hands[hand]?.position?.top;
                this.options.buttons.spin.orientations[mode].hands[hand].position.right = data?.orientations[mode]?.hands[hand]?.position?.right;
            }
        }
    }

    /**
     * Is the current device sound options set?
     */
    isSoundSet() {
        // return false if device.sound is undefined or if there is no session and player id set
        return !!(this.options.devices[getDeviceStr()].sound !== undefined && this.isAllowed());
    }

    /**
     * Return current sound option for current device.
     */
    getSound() {
        return this.options.devices[getDeviceStr()].sound;
    }

    /**
     * Set default value of this.options
     */
    initOptions() {
        this.options = {
            bet: this.wallet.getCreditBet(),
            coin: this.wallet.getCoin(),
            lines_count: this.wallet.getLinesCount(),
            devices: {
                undefined: {
                    sound: undefined,
                    hand: "right"
                },
                desktop: {
                    sound: undefined,
                    hand: "right"
                },
                mobile: {
                    sound: false,
                    hand: "right"
                },
                tablet: {
                    sound: undefined,
                    hand: "right"
                }
            },
            buttons: {
                spin: {
                    orientations: {
                        landscape: {
                            hands: {
                                right: {
                                    position: {
                                        top: null,
                                        right: null
                                    }
                                },
                                left: {
                                    position: {
                                        top: null,
                                        right: null
                                    }
                                }
                            }
                        },
                        portrait: {
                            hands: {
                                right: {
                                    position: {
                                        top: null,
                                        right: null
                                    }
                                },
                                left: {
                                    position: {
                                        top: null,
                                        right: null
                                    }
                                }
                            }
                        }
                    }
                }
            },
            updated: ""
        };
    }
}


interface IPlayerOptionsData {
    bet: number,
    coin: number,
    lines_count: number,
    devices: {
        undefined: {
            sound: boolean,
            hand: string
        },
        desktop: {
            sound: boolean,
            hand: string
        },
        mobile: {
            sound: boolean,
            hand: string
        },
        tablet: {
            sound: boolean,
            hand: string
        }
    },
    buttons: {
        spin: {
            orientations: {
                landscape: {
                    hands: {
                        right: {
                            position: {
                                top: number|null,
                                right: number|null
                            }
                        },
                        left: {
                            position: {
                                top: number|null,
                                right: number|null
                            }
                        }
                    }
                },
                portrait: {
                    hands: {
                        right: {
                            position: {
                                top: number|null,
                                right: number|null
                            }
                        },
                        left: {
                            position: {
                                top: number|null,
                                right: number|null
                            }
                        }
                    }
                }
            }
        }
    },
    updated: string,
}

export default PlayerOptions;
